import React, { useState, useEffect } from "react";
import { useParams, useSearchParams, Link } from "react-router-dom";
import { format, parse } from "date-fns";
import { tr } from "date-fns/locale";

function ReservationPage() {
    const { id } = useParams();
    const [searchParams] = useSearchParams();
    const [yacht, setYacht] = useState(null);
    const [loading, setLoading] = useState(true);

    // Form state for user contact information
    const [formData, setFormData] = useState({
        fullName: '',
        email: '',
        phone: '',
        guestCount: '',
        notes: ''
    });

    // URL'den tarihleri al
    const startDate = searchParams.get("startDate");
    const endDate = searchParams.get("endDate");

    // Tarihleri formatlı göstermek için
    const formatDate = (dateStr) => {
        if (!dateStr) return null;
        const date = parse(dateStr, "yyyy-MM-dd", new Date());
        return format(date, "d MMMM yyyy", { locale: tr });
    };

    useEffect(() => {
        // Load user data from localStorage
        const userData = localStorage.getItem('user');
        if (userData) {
            try {
                const user = JSON.parse(userData);
                setFormData(prev => ({
                    ...prev,
                    fullName: `${user.name || ''} ${user.surname || ''}`.trim(),
                    email: user.email || '',
                    phone: user.phoneNumber || ''
                }));
            } catch (error) {
                console.error('Kullanıcı verisi parse hatası:', error);
            }
        }

        const fetchYachtDetails = async () => {
            try {
                const token = localStorage.getItem('authToken');

                if (!token) {
                    console.error('Kullanıcı girişi yapılmamış');
                    setLoading(false);
                    return;
                }

                const response = await fetch(`/rest/Yacht/getYachtById?id=${id}`, {
                    method: "GET",
                    headers: {
                        "Content-Type": "application/json",
                        "Authorization": `Bearer ${token}`
                    }
                });

                if (!response.ok) {
                    throw new Error(`HTTP error! status: ${response.status}`);
                }

                const yachtData = await response.json();
                setYacht(yachtData);
                setLoading(false);

            } catch (error) {
                console.error("Yat detayları yüklenirken hata:", error);
                setLoading(false);
            }
        };

        fetchYachtDetails();
    }, [id]);

    // Handle form input changes
    const handleInputChange = (e) => {
        const { name, value } = e.target;
        setFormData(prev => ({
            ...prev,
            [name]: value
        }));
    };

    if (loading) {
        return (
            <div className="min-h-screen relative">
                <div
                    className="fixed inset-0 bg-cover bg-center bg-no-repeat"
                    style={{
                        backgroundImage: `url(/images/yacht-bg.jpg)`,
                        filter: "brightness(0.9)",
                    }}
                />
                <div className="fixed inset-0 bg-gradient-to-b from-gray-900/90 via-gray-900/70 to-gray-900/90" />
                <div className="relative z-10 min-h-screen flex items-center justify-center">
                    <div className="animate-spin rounded-full h-12 w-12 border-t-2 border-b-2 border-blue-500"></div>
                </div>
            </div>
        );
    }

    return (
        <div className="min-h-screen relative">
            {/* Background Image */}
            <div
                className="fixed inset-0 bg-cover bg-center bg-no-repeat"
                style={{
                    backgroundImage: `url(/images/yacht-bg-4.jpg)`,
                    filter: "brightness(0.9)",
                }}
            />

            {/* Gradient Overlay */}
            <div className="fixed inset-0 bg-gradient-to-b from-gray-900/90 via-gray-900/70 to-gray-900/90" />

            {/* Content */}
            <div className="relative z-10">
                <div className="max-w-4xl mx-auto px-4 py-16">
                    {/* Geri Dönüş Linki */}
                    <Link
                        to={`/yachts/${id}`}
                        className="inline-flex items-center text-white/60 hover:text-white mb-8"
                    >
                        <svg
                            className="w-5 h-5 mr-2"
                            fill="none"
                            stroke="currentColor"
                            viewBox="0 0 24 24"
                        >
                            <path
                                strokeLinecap="round"
                                strokeLinejoin="round"
                                strokeWidth={2}
                                d="M10 19l-7-7m0 0l7-7m-7 7h18"
                            />
                        </svg>
                        Yat Detaylarına Dön
                    </Link>

                    {/* Rezervasyon Detayları */}
                    <div className="bg-white/5 backdrop-blur-xl rounded-2xl p-8 border border-white/10">
                        <h1 className="text-3xl font-bold text-white mb-6">
                            Rezervasyon Detayları
                        </h1>

                        <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
                            {/* Yat Bilgileri */}
                            <div className="space-y-4">
                                <h2 className="text-xl font-semibold text-white">
                                    Yat Bilgileri
                                </h2>
                                <div className="space-y-2 text-white/60">
                                    <p>
                                        <span className="text-white/40">
                                            Yat:
                                        </span>{" "}
                                        {yacht.name}
                                    </p>
                                    <p>
                                        <span className="text-white/40">
                                            Kaptan:
                                        </span>{" "}
                                        {yacht.captain}
                                    </p>
                                    <p>
                                        <span className="text-white/40">
                                            Kapasite:
                                        </span>{" "}
                                        {yacht.capacity} misafir
                                    </p>
                                    <p>
                                        <span className="text-white/40">
                                            Uzunluk:
                                        </span>{" "}
                                        {yacht.length}m
                                    </p>
                                </div>
                            </div>

                            {/* Rezervasyon Tarihleri */}
                            <div className="space-y-4">
                                <h2 className="text-xl font-semibold text-white">
                                    Seçilen Tarihler
                                </h2>
                                <div className="space-y-2 text-white/60">
                                    <p>
                                        <span className="text-white/40">
                                            Başlangıç:
                                        </span>{" "}
                                        {formatDate(startDate) ||
                                            "Tarih seçilmedi"}
                                    </p>
                                    <p>
                                        <span className="text-white/40">
                                            Bitiş:
                                        </span>{" "}
                                        {formatDate(endDate) ||
                                            "Tarih seçilmedi"}
                                    </p>
                                </div>
                            </div>
                        </div>

                        {/* Rezervasyon Formu */}
                        <div className="mt-8 space-y-6">
                            <h2 className="text-xl font-semibold text-white">
                                İletişim Bilgileri
                            </h2>
                            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                                <input
                                    type="text"
                                    name="fullName"
                                    placeholder="Ad Soyad *"
                                    value={formData.fullName}
                                    onChange={handleInputChange}
                                    required
                                    className="w-full px-4 py-3 bg-white/5 border border-white/10 rounded-xl text-white placeholder-white/30 focus:outline-none focus:border-blue-500 invalid:border-red-500/50"
                                />
                                <input
                                    type="email"
                                    name="email"
                                    placeholder="E-posta *"
                                    value={formData.email}
                                    onChange={handleInputChange}
                                    required
                                    className="w-full px-4 py-3 bg-white/5 border border-white/10 rounded-xl text-white placeholder-white/30 focus:outline-none focus:border-blue-500 invalid:border-red-500/50"
                                />
                                <input
                                    type="tel"
                                    name="phone"
                                    placeholder="Telefon *"
                                    value={formData.phone}
                                    onChange={handleInputChange}
                                    required
                                    className="w-full px-4 py-3 bg-white/5 border border-white/10 rounded-xl text-white placeholder-white/30 focus:outline-none focus:border-blue-500 invalid:border-red-500/50"
                                />
                                <input
                                    type="number"
                                    name="guestCount"
                                    placeholder="Misafir Sayısı *"
                                    value={formData.guestCount}
                                    onChange={handleInputChange}
                                    required
                                    min="1"
                                    max={yacht?.capacity || 10}
                                    className="w-full px-4 py-3 bg-white/5 border border-white/10 rounded-xl text-white placeholder-white/30 focus:outline-none focus:border-blue-500 invalid:border-red-500/50"
                                />
                            </div>
                            <textarea
                                name="notes"
                                placeholder="Ekstra Notlar"
                                value={formData.notes}
                                onChange={handleInputChange}
                                rows="4"
                                className="w-full px-4 py-3 bg-white/5 border border-white/10 rounded-xl text-white placeholder-white/30 focus:outline-none focus:border-blue-500 resize-none"
                            ></textarea>
                        </div>

                        {/* Onay Butonu */}
                        <button className="w-full mt-8 py-4 px-6 bg-gradient-to-r from-blue-600 to-blue-500 text-white rounded-xl hover:shadow-lg hover:shadow-blue-500/30 transition-all duration-300 transform hover:-translate-y-0.5 text-lg font-semibold">
                            Ödeme Adımı İle Devam Et
                        </button>
                    </div>
                </div>
            </div>
        </div>
    );
}

export default ReservationPage;
